export const COMMEND3_TITLE: string = "Commend";
export const COMMEND3_PATH: string = "/gms/commend-v3";

export const NOT_YET_AVAILABLE: string = "Not available yet";

export const RECOMMENDER_CREATE: string = "recommender_create";
export const RECOMMENDER_READ: string = "recommender_read";
export const RECOMMENDER_UPDATE: string = "recommender_update";
export const RECOMMENDER_DELETE: string = "recommender_delete";
export const RECOMMENDER_PROMOTE: string = "recommender_promote";

export const MODELS_TAB: string = "models";
export const STRATEGIES_TAB: string = "strategies";

export const AUTO_REFRESH_INTERVAL_MS: number = 2 * 60 * 1000;

export const PAGE_SIZE_OPTIONS: number[] = [10, 25];
export const DEFAULT_PAGE_SIZE: number = 10;

export const NOT_CONFIGURED_LABEL: string = "Not configured";
export const NO_WIDGET_LABEL: string = "—";

export const UNPROMOTE_BLOCKED_MESSAGE: string =
  "Cannot Unpromote Model. Please update the widget mapping before unpromoting.";

export const PROMOTION_STATUS = {
  NOT_PROMOTED: "Not Promoted",
  PROMOTING: "Promoting",
  PROMOTED: "Promoted",
};

export const STRATEGIES_SECTION_TITLE: string = "Main strategies";

export const STRATEGIES_INFO_NOTE: string =
  "Strategies are generated for the entire product catalog and shared across all models. Generate a strategy here, then connect it to a model during model creation.";

export const PREVIEW_SEARCH_PLACEHOLDER: string = "Search product name or SKU…";

export const PREVIEW_EMPTY_STATE: string =
  "Search a product name or SKU above to see the recommendations generated for it.";

export const PREVIEW_NO_MATCH: string = "No product matches that name or SKU.";

export const GENERATION_STATUS = {
  NOT_GENERATED: "Not Generated",
  GENERATING: "Generating",
  GENERATED: "Generated",
  FAILED: "Failed",
};

export const NOT_GENERATED_LABEL: string = "Not generated yet";
export const GENERATING_LABEL: string = "Generating…";

export const PREVIEW_DISABLED_REASON: string =
  "Generate this strategy first to preview its recommendations";

export const MODEL_TYPE_CO_SELLING: string = "Co-Selling";
export const MODEL_TYPE_SUBSTITUTION: string = "Substitution";

export type Commend3ModelTypeOption = {
  value: string;
  label: string;
  description: string;
};

export const MODEL_TYPE_OPTIONS: Commend3ModelTypeOption[] = [
  {
    value: MODEL_TYPE_CO_SELLING,
    label: "Co-selling",
    description:
      "Recommends other products that can be purchased along with the base product.",
  },
  {
    value: MODEL_TYPE_SUBSTITUTION,
    label: "Substitution",
    description: "Recommends same-category alternatives.",
  },
];

export const CREATE_MODEL_TITLE: string = "Create model";

export const IN_SCOPE_CHIP: string = "In scope";
export const IN_SCOPE_LABEL: string = "Base products this strategy covers";
export const OUT_OF_SCOPE_CHIP: string = "Out of scope";
export const OUT_OF_SCOPE_LABEL: string = "Base products with no eligible results";

export const ENGINE_CONTROLLED_NOTE: string =
  "Mode order and eligibility are engine-controlled — only the widget title is configurable. To regenerate a strategy, go to the Strategies tab.";

export const STRATEGY_SECTION_LABEL: string = "Selected Strategy";

export const SUBSTITUTION_LOCKED_NOTE: string =
  "Substitution models always use Similarity — nothing to choose here.";
export const PROMOTED_LOCK_TOAST: string =
  "This model is promoted and cannot be edited. Unpromote it to make changes.";

export const AUTOSAVE_DEBOUNCE_MS: number = 800;

export const STRATEGY_CHANGE_TITLE: string = "Change strategy?";
export const STRATEGY_CHANGE_ACTION: string = "Change strategy";
export const strategyChangeMessage = (strategyName: string, promoted: boolean): string =>
  `Switching to “${strategyName}” replaces the modes and widget titles for this model. The change saves automatically.` +
  (promoted
    ? " This model is already promoted — re-promote it for the new strategy to go live."
    : "");

export const AUTOSAVE_SAVED_TOAST: string = "Saved";

export const DYNAMIC_CATEGORY_MODE: string = "Popular in top category";
export const DYNAMIC_CATEGORY_SUFFIX: string = "most popular category";
export const DYNAMIC_CATEGORY_HELPER: string =
  "Not resolved yet — generate this strategy and the channel's most popular category will be filled in here.";

export const MODEL_SEARCH_PLACEHOLDER: string = "Search";
export const MODEL_SEARCH_DEBOUNCE_MS: number = 400;

export const FILTER_PANEL_TITLE: string = "Filter";
export const FILTER_CLEAR_ALL: string = "Clear all";
export const FILTER_DONE: string = "Done";

export const NOT_MAPPED_LABEL: string = "Not mapped";


export const filterResultText = (count: number): string =>
  `${count} ${count === 1 ? "model" : "models"}`;

export const CREATE_MODEL_NAME_PLACEHOLDER: string = "e.g. GAG — Box 4 model";
export const RENAME_MODEL_TITLE: string = "Rename model";

export const DELETE_CONFIRM_TITLE: string = "Delete model?";
export const DELETE_CONFIRM_ACTION: string = "Delete";
export const deleteConfirmBody = (name: string): string =>
  `Are you sure you want to permanently delete “${name}”? This action cannot be undone.`;

export const GENERATION_LIMIT_NOTE: string =
  "Monthly generation limit reached. Additional generations are available only with a Subscription.";

export const STALE_PROMOTION_HINT: string =
  "A newer generation is available. Promote to push the updated recommendations live.";

export const READ_ONLY_TITLE_LIVE: string = "This model is live and cannot be edited";
export const READ_ONLY_TITLE_PROMOTED: string = "This model is promoted and cannot be edited";
export const readOnlyNote = (widgetName: string | null): string =>
  widgetName
    ? `It is promoted and mapped to ${widgetName}, so its strategy and widget titles are locked. Unpromote the model to make changes.`
    : "It is promoted, so its strategy and widget titles are locked. Unpromote the model to make changes.";

export const MODEL_CHIP_LABEL: string = "Model";
export const AUTOSAVE_IDLE_LABEL: string = "Autosave on";
export const AUTOSAVE_SAVED_LABEL: string = "Saved";

export const READ_ONLY_TITLE_HINT: string =
  "This model is promoted — unpromote it to edit titles";

export const IN_SCOPE_NOTE: string =
  "Modes run top to bottom. The first mode with enough eligible results fills the widget.";
export const OUT_OF_SCOPE_NOTE: string =
  "A single fallback keeps the widget populated when nothing in scope qualifies.";

export const DYNAMIC_TOKEN_HINT: string =
  "Most popular category for this channel — set automatically at generation time, not editable";

export const STALE_GENERATION_HINT: string =
  "This strategy was generated again after the model was promoted. Promote to push the new recommendations live.";
export const MODELS_TABLE_MIN_WIDTH: string = "1190px";

export const ROW_ACTION_NO_CREATE_PERMISSION: string =
  "You do not have permission to copy a model";
export const ROW_ACTION_NO_DELETE_PERMISSION: string =
  "You do not have permission to delete a model";
export const ROW_ACTION_BUSY: string = "Another action on this model is still running";

export const ALL_MODELS_LIMIT: number = 500;

export type Commend3FacetOption = {
  label: string;
  value: string;
};

export type Commend3Facet = {
  key: string;
  label: string;
  options: Commend3FacetOption[];
};

export const FILTER_FACETS: Commend3Facet[] = [
  {
    key: "promotion",
    label: "Promotion Status",
    options: [
      { label: "Live", value: "live" },
      { label: "Promoted", value: "promoted" },
      { label: "Not promoted", value: "not-promoted" },
    ],
  },
  {
    key: "generation",
    label: "Strategy Generation Status",
    options: [
      { label: "Generated", value: "Generated" },
      { label: "Not generated", value: "Not generated" },
    ],
  },
  {
    key: "type",
    label: "Model Type",
    options: [
      { label: "Co-selling", value: MODEL_TYPE_CO_SELLING },
      { label: "Substitution", value: MODEL_TYPE_SUBSTITUTION },
    ],
  },
  {
    key: "strategy",
    label: "Strategy Assigned",
    options: [
      { label: "Co-Purchase Recommendations", value: "Co-Purchase Recommendations" },
      { label: "Compatibility Recommendations", value: "Compatibility Recommendations" },
      { label: "Similarity", value: "Similarity" },
    ],
  },
  {
    key: "widget",
    label: "Widget",
    options: [
      { label: "Widget 1", value: "Widget 1" },
      { label: "Widget 2", value: "Widget 2" },
      { label: "Widget 3", value: "Widget 3" },
      { label: NOT_MAPPED_LABEL, value: NOT_MAPPED_LABEL },
    ],
  },
];
